// Environment configuration
export interface EnvironmentConfig {
  apiBaseUrl: string
  apiTimeout: number
  appTitle: string
  environment: 'development' | 'production' | 'local'
  isDevelopment: boolean
  isProduction: boolean
}

// Default configuration
const defaultConfig: EnvironmentConfig = {
  apiBaseUrl: '/api',
  apiTimeout: 30000,
  appTitle: 'Dynamo Backoffice',
  environment: 'development',
  isDevelopment: true,
  isProduction: false,
}

// Environment-specific configurations
const configs: Record<string, Partial<EnvironmentConfig>> = {
  development: {
    apiBaseUrl: '/api',
    apiTimeout: 30000,
    appTitle: 'Dynamo Backoffice (Development)',
    environment: 'development',
    isDevelopment: true,
    isProduction: false,
  },
  production: {
    apiBaseUrl: 'https://api.dynamo-backoffice.com',
    apiTimeout: 30000,
    appTitle: 'Dynamo Backoffice',
    environment: 'production',
    isDevelopment: false,
    isProduction: true,
  },
  local: {
    apiBaseUrl: 'http://localhost:3001/api',
    apiTimeout: 30000,
    appTitle: 'Dynamo Backoffice (Local)',
    environment: 'local',
    isDevelopment: true,
    isProduction: false,
  },
}

// Get current environment
function getCurrentEnvironment(): string {
  // Check for Vite environment variables first
  if (import.meta.env?.VITE_APP_ENV) {
    return import.meta.env.VITE_APP_ENV
  }
  
  // Check for Vite mode
  if (import.meta.env?.MODE) {
    return import.meta.env.MODE
  }
  
  // Default to development
  return 'development'
}

// Get environment-specific configuration
function getEnvironmentConfig(): EnvironmentConfig {
  const env = getCurrentEnvironment()
  const envConfig = configs[env] || configs.development
  
  // Override with Vite environment variables if available
  const viteConfig: Partial<EnvironmentConfig> = {}
  
  if (import.meta.env?.VITE_API_BASE_URL) {
    viteConfig.apiBaseUrl = import.meta.env.VITE_API_BASE_URL
  }
  
  if (import.meta.env?.VITE_API_TIMEOUT) {
    viteConfig.apiTimeout = parseInt(import.meta.env.VITE_API_TIMEOUT, 10)
  }
  
  if (import.meta.env?.VITE_APP_TITLE) {
    viteConfig.appTitle = import.meta.env.VITE_APP_TITLE
  }
  
  if (import.meta.env?.VITE_APP_ENV) {
    viteConfig.environment = import.meta.env.VITE_APP_ENV as any
  }
  
  // Merge configurations
  return {
    ...defaultConfig,
    ...envConfig,
    ...viteConfig,
  }
}

// Export the configuration
export const config = getEnvironmentConfig()

// Export individual values for convenience
export const {
  apiBaseUrl,
  apiTimeout,
  appTitle,
  environment,
  isDevelopment,
  isProduction,
} = config

// Log configuration in development
if (isDevelopment) {
  console.log('🔧 Environment Configuration:', {
    environment,
    apiBaseUrl,
    apiTimeout,
    appTitle,
  })
}
